import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Play, Eye, EyeOff, Languages, ExternalLink } from "lucide-react";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { API } from "../../config";

export default function OfficinaTab({ dm, authHeader }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [archStart, setArchStart] = useState("");
  const [archPages, setArchPages] = useState(2);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/officina/status`, { headers: authHeader() });
      setStatus(data);
    } catch { /* noop */ }
    setLoading(false);
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (enabled) => {
    try {
      await axios.post(`${API}/admin/officina/toggle`, { enabled }, { headers: authHeader() });
      toast.success(enabled ? "Daily import enabled" : "Daily import disabled");
      load();
    } catch { toast.error("Toggle failed"); }
  };

  const importNow = async () => {
    setBusy("import");
    try {
      const { data } = await axios.post(`${API}/admin/officina/import-now`, {}, { headers: authHeader() });
      data.started ? toast.success(data.message) : toast.info(data.message);
      setTimeout(load, 5000);
    } catch { toast.error("Import failed to start"); }
    setBusy("");
  };

  const importArchive = async () => {
    setBusy("archive");
    try {
      const start = parseInt(archStart, 10) || suggestedPage;
      const { data } = await axios.post(`${API}/admin/officina/import-now`,
        { pages: Math.max(1, Math.min(parseInt(archPages, 10) || 1, 5)), start_page: start },
        { headers: authHeader() });
      data.started
        ? toast.success(`Archive import started (pages ${data.start_page}–${data.start_page + data.pages - 1})`)
        : toast.info(data.message);
      setTimeout(load, 5000);
    } catch { toast.error("Archive import failed to start"); }
    setBusy("");
  };

  const retranslate = async (id) => {
    setBusy(id);
    try {
      await axios.post(`${API}/admin/officina/articles/${id}/retranslate`, {}, { headers: authHeader() });
      toast.success("Translated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Translation failed"); }
    setBusy("");
  };

  const setVisibility = async (id, hidden) => {
    try {
      await axios.post(`${API}/admin/officina/articles/${id}/visibility`, { hidden }, { headers: authHeader() });
      toast.success(hidden ? "Hidden from public page" : "Published");
      load();
    } catch { toast.error("Update failed"); }
  };

  const card = `rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`;
  const cardStyle = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };
  const text = dm ? "text-slate-100" : "text-[#191c1d]";
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";
  const lr = status?.last_run;
  const suggestedPage = Math.floor((status?.counts?.total ?? 0) / 10) + 1;

  return (
    <TabsContent value="officina">
      <div className={card} style={cardStyle} data-testid="officina-admin-card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-2">
            <Languages className={`w-5 h-5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
            <h3 className={`editorial-heading text-xl font-medium ${text}`}>Officina Turistica Import</h3>
          </div>
          <div className="flex items-center gap-4">
            <label className={`flex items-center gap-2 text-xs font-medium ${muted}`}>
              Daily import
              <Switch checked={!!status?.config?.enabled} onCheckedChange={toggle} data-testid="officina-toggle-switch" />
            </label>
            <button
              onClick={importNow}
              disabled={busy === "import"}
              data-testid="officina-import-now-btn"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${dm ? "bg-cyan-400/15 text-cyan-300 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}>
              <Play className="w-3.5 h-3.5" /> Import now
            </button>
            <button onClick={load} className={muted} title="Refresh" data-testid="officina-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-xs ${muted}`}>
          <div><span className={`block text-lg font-semibold ${text}`} data-testid="officina-count-total">{status?.counts?.total ?? "—"}</span>Imported</div>
          <div><span className={`block text-lg font-semibold ${text}`} data-testid="officina-count-pending">{status?.counts?.pending_translation ?? "—"}</span>Pending translation</div>
          <div><span className={`block text-lg font-semibold ${text}`}>{status?.counts?.hidden ?? "—"}</span>Hidden</div>
          <div>
            <span className={`block text-lg font-semibold ${text}`}>
              {lr?.finished_at ? new Date(lr.finished_at).toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "never"}
            </span>
            Last run {lr ? `(+${lr.imported ?? 0} new, ${lr.translated ?? 0} translated${(lr.errors || []).length ? `, ${lr.errors.length} errors` : ""})` : ""}
          </div>
        </div>

        <div className={`flex flex-wrap items-end gap-3 mb-5 p-3 rounded-xl ${dm ? "bg-white/3" : "bg-black/3"}`} data-testid="officina-archive-panel">
          <div>
            <label className={`block text-[11px] font-medium mb-1 ${muted}`}>From feed page</label>
            <input type="number" min="1" max="100" value={archStart} placeholder={String(suggestedPage)}
              onChange={(e) => setArchStart(e.target.value)}
              data-testid="officina-archive-start-input"
              className={`w-24 px-2 py-1.5 rounded-lg text-sm border bg-transparent ${dm ? "border-slate-600 text-slate-200" : "border-[#c6c8c9] text-[#191c1d]"}`} />
          </div>
          <div>
            <label className={`block text-[11px] font-medium mb-1 ${muted}`}>Pages (10 posts each, max 5)</label>
            <input type="number" min="1" max="5" value={archPages}
              onChange={(e) => setArchPages(e.target.value)}
              data-testid="officina-archive-pages-input"
              className={`w-24 px-2 py-1.5 rounded-lg text-sm border bg-transparent ${dm ? "border-slate-600 text-slate-200" : "border-[#c6c8c9] text-[#191c1d]"}`} />
          </div>
          <button onClick={importArchive} disabled={busy === "archive"}
            data-testid="officina-archive-import-btn"
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50 ${dm ? "bg-amber-400/15 text-amber-300 hover:bg-amber-400/25" : "bg-amber-600/10 text-amber-700 hover:bg-amber-600/20"}`}>
            {busy === "archive" ? "Starting…" : "Import older posts"}
          </button>
          <p className={`text-[11px] ${muted}`}>Pulls deeper blog archive on demand. Already-imported posts are skipped. Suggested next page: {suggestedPage}.</p>
        </div>

        <div className="space-y-1" data-testid="officina-article-list">
          {(status?.articles || []).length === 0 ? (
            <p className={`text-sm text-center py-8 ${muted}`}>No articles imported yet — hit &quot;Import now&quot;.</p>
          ) : (status?.articles || []).map((a) => (
            <div key={a.id} className={`flex items-center gap-3 py-2.5 border-b last:border-0 ${dm ? "border-white/4" : "border-black/4"}`} data-testid={`officina-admin-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${text}`}>{a.title_en || a.title_it}</p>
                <p className={`text-xs ${muted}`}>
                  {a.author} · {a.published_at ? new Date(a.published_at).toLocaleDateString() : ""} · {a.full_text_source === "firecrawl" ? "full text" : "excerpt"}
                  {a.translation_status !== "done" && <span className="ml-2 text-amber-500 font-semibold">pending translation</span>}
                  {a.hidden && <span className="ml-2 text-rose-500 font-semibold">hidden</span>}
                </p>
              </div>
              <a href={a.url} target="_blank" rel="noreferrer" className={muted} title="Original post"><ExternalLink className="w-3.5 h-3.5" /></a>
              {a.translation_status !== "done" && (
                <button onClick={() => retranslate(a.id)} disabled={busy === a.id}
                  data-testid={`officina-retranslate-btn-${a.id}`}
                  className={`text-xs font-semibold px-2 py-1 rounded disabled:opacity-50 ${dm ? "text-cyan-300 hover:bg-white/5" : "text-[#006874] hover:bg-black/5"}`}>
                  {busy === a.id ? "Translating…" : "Translate"}
                </button>
              )}
              <button onClick={() => setVisibility(a.id, !a.hidden)}
                data-testid={`officina-visibility-btn-${a.id}`}
                className={`p-1 rounded ${muted} hover:opacity-70`} title={a.hidden ? "Publish" : "Hide"}>
                {a.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      </div>
    </TabsContent>
  );
}
